import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Header from '../components/Header';
import NavTabs from '../components/NavTabs';

const FEATURES = [
  {
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
          d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
      </svg>
    ),
    title: 'Full Facility Details',
    description:
      'See complete bed breakdowns by type — withdrawal management, residential SUD, eval & treatment, crisis stabilization, and more. Guest users see only name, city, and phone. Subscribers see everything.',
  },
  {
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
          d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
      </svg>
    ),
    title: 'Save Favorites',
    description:
      'Bookmark facilities you refer to often. Your favorites persist across sessions and devices. Org accounts share a single favorites list across all staff — no duplicate work.',
  },
  {
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
          d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
      </svg>
    ),
    title: 'Private Notes',
    description:
      'Add your own notes directly to any facility — referral tips, contact preferences, wait time observations. Notes are private to you or your organization and never visible to other users.',
  },
  {
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
          d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
      </svg>
    ),
    title: 'Bed Alerts',
    description:
      'Set alerts on specific facilities and get notified when their bed counts change. Stay ahead of availability without manually checking the directory every day.',
  },
  {
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
          d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
      </svg>
    ),
    title: 'Verified Agency Badge',
    description:
      'Organization subscribers can link their account to their facility in the directory. Once approved, your facility listing displays a verified badge — signaling to referrers that your information is actively maintained by someone at your agency.',
  },
  {
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
          d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>
    ),
    title: 'Up to 8 Staff Logins',
    description:
      'Organization accounts support up to 8 active logins at a time — one subscription covering your whole team. If a 9th device logs in, the longest-idle session is automatically bumped.',
  },
  {
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
          d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
          d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>
    ),
    title: 'ZIP / Radius Search',
    description:
      'Find facilities within a specific distance from any Washington ZIP code. Essential for placing clients when county-level filtering isn\'t precise enough.',
  },
];

const TIERS = [
  {
    name: 'Guest',
    price: 'Free',
    color: 'border-gray-200',
    badge: 'bg-gray-100 text-gray-600',
    features: [
      { label: 'Search & filter facilities', yes: true },
      { label: 'Facility name, city & phone', yes: true },
      { label: 'Full bed count details', yes: false },
      { label: 'Favorites', yes: false },
      { label: 'Private notes', yes: false },
      { label: 'Bed alerts', yes: false },
      { label: 'ZIP / radius search', yes: false },
      { label: 'Verified agency badge', yes: false },
      { label: 'Up to 8 staff logins', yes: false },
    ],
  },
  {
    name: 'Individual',
    price: '$9.99 / mo',
    color: 'border-blue-400',
    badge: 'bg-blue-100 text-blue-700',
    highlight: true,
    features: [
      { label: 'Search & filter facilities', yes: true },
      { label: 'Facility name, city & phone', yes: true },
      { label: 'Full bed count details', yes: true },
      { label: 'Favorites', yes: true },
      { label: 'Private notes', yes: true },
      { label: 'Bed alerts', yes: true },
      { label: 'ZIP / radius search', yes: true },
      { label: 'Verified agency badge', yes: false },
      { label: 'Up to 8 staff logins', yes: false },
    ],
  },
  {
    name: 'Organization',
    price: '$25 / mo',
    color: 'border-purple-400',
    badge: 'bg-purple-100 text-purple-700',
    features: [
      { label: 'Search & filter facilities', yes: true },
      { label: 'Facility name, city & phone', yes: true },
      { label: 'Full bed count details', yes: true },
      { label: 'Shared favorites (all staff)', yes: true },
      { label: 'Shared notes (all staff)', yes: true },
      { label: 'Bed alerts', yes: true },
      { label: 'ZIP / radius search', yes: true },
      { label: 'Verified agency badge', yes: true },
      { label: 'Up to 8 staff logins', yes: true },
    ],
  },
];

function Check() {
  return (
    <svg className="w-4 h-4 text-green-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
    </svg>
  );
}

function X() {
  return (
    <svg className="w-4 h-4 text-gray-300 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
    </svg>
  );
}

export default function WhySubscribe() {
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col">
      <Helmet>
        <title>Why Subscribe | WA BHA Treatment Finder</title>
        <meta name="description" content="See what's included with a WA BHA Treatment Finder subscription — full bed details, favorites, notes, bed alerts, and radius search." />
      </Helmet>
      <Header right={null} />
      <NavTabs />

      <div className="flex-1 max-w-4xl mx-auto w-full px-4 py-10">
        <div className="mb-10">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Why Subscribe?</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Guest access lets you search and find facilities. A subscription gives you the tools to actually work with them.
          </p>
        </div>

        {/* Feature cards */}
        <div className="grid sm:grid-cols-2 gap-4 mb-12">
          {FEATURES.map((f) => (
            <div key={f.title} className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 px-5 py-5 flex gap-4">
              <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-blue-50 dark:bg-blue-900/30 flex items-center justify-center text-blue-600 dark:text-blue-400">
                {f.icon}
              </div>
              <div>
                <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-1">{f.title}</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed">{f.description}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Tier comparison */}
        <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-4">Plans at a glance</h2>
        <div className="grid sm:grid-cols-3 gap-4 mb-10">
          {TIERS.map((tier) => (
            <div
              key={tier.name}
              className={`bg-white dark:bg-gray-800 rounded-xl border-2 ${tier.color} dark:border-opacity-60 px-5 py-5 flex flex-col`}
            >
              <div className="flex items-center justify-between mb-1">
                <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${tier.badge}`}>{tier.name}</span>
              </div>
              <p className="text-xl font-bold text-gray-900 dark:text-gray-100 mt-2 mb-4">{tier.price}</p>
              <ul className="space-y-2 flex-1">
                {tier.features.map((item) => (
                  <li key={item.label} className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                    {item.yes ? <Check /> : <X />}
                    {item.label}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="bg-blue-600 rounded-xl px-6 py-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <p className="text-white font-semibold text-base">Ready to get full access?</p>
            <p className="text-blue-100 text-sm mt-0.5">Start a subscription or ask your admin about a comp account.</p>
          </div>
          <Link
            to="/subscribe"
            className="flex-shrink-0 bg-white text-blue-700 hover:bg-blue-50 font-semibold text-sm px-5 py-2.5 rounded-lg transition-colors"
          >
            View plans
          </Link>
        </div>

        {/* Development notice */}
        <div className="mt-6 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-xl px-6 py-5">
          <p className="text-sm font-semibold text-amber-800 dark:text-amber-300 mb-1">This tool is always growing</p>
          <p className="text-sm text-amber-700 dark:text-amber-400">
            WA BHA Treatment Finder is in active development — many more features are in the works. Have a suggestion or idea?{' '}
            <a href="mailto:watreatmentfinder@gmail.com" className="font-medium underline hover:text-amber-900 dark:hover:text-amber-200">
              Send it our way — watreatmentfinder@gmail.com
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
